import { FunctionComponent, h } from "preact"
import { useState, useEffect } from "preact/hooks"
import { BsPencil, BsTrash, BsEye } from "react-icons/bs"
import { useModule } from "../../../hooks/useModule"
import useAuth from "../../../hooks/useAuth"
import usePagination from "../../../hooks/usePagination"
import useModal from "../../../hooks/useModal"
import { useConfirm } from "../../../context/useConfirm"
import { can, canOneOf } from "../../../utils/access"
import { Permission } from "../../../enum/permissions.enum"
import { Button } from "../../../parts/buttons"
import Container from "../../../parts/container"
import Pagination from "../../../components/pagination"
import TopBar from "../../../components/topbar"
import Breadcrumb from "../../../components/topbar/breadcrumb"
import Spinner from "../../../components/spinner"
import SearchBox from "../../../components/TopFilter/search-box"
import AddModule from "./add"
import EditModule from "./edit"
import ModuleDetail from "./detail"
import { useTranslation } from "react-i18next"

type SettingsModuleProps = h.JSX.HTMLAttributes<HTMLDivElement>

const SettingsModule: FunctionComponent<SettingsModuleProps> = () => {
  const { t } = useTranslation()
  const { fetcher } = useAuth()
  const confirmation = useConfirm()
  const { data, mutate } = useModule()
  const { show: showAdd, onHide: hideAdd, onShow: onShowAdd } = useModal()
  const { show: showEdit, onHide: hideEdit, onShow: onShowEdit } = useModal()
  const { show: showDetail, onHide: hideDetail, onShow: onShowDetail } = useModal()
  const [modalData, setModalData] = useState()
  const [filteredData, setFilteredData] = useState(data)
  const [search, setSearch] = useState("")

  const showActionColumn = canOneOf([Permission.SettingsModuleDelete, Permission.SettingsModuleDetail, Permission.SettingsModuleEdit])

  filteredData?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))

  const pages = usePagination({ count: filteredData?.length || 0, step: 20 })
  const dataTable = filteredData?.slice(pages.output, pages.output + pages.step)

  const handleEdit = (data) => {
    setModalData(data)
    onShowEdit()
  }
  const handleDetail = (data) => {
    setModalData(data)
    onShowDetail()
  }
  const handleDelete = (id: string, name: string) => {
    confirmation(
      t("delete_module_confirm_msg", { module_name: name }),
      (out) => {
        if (out)
          fetcher()
            .delete(`/module/${id}`)
            .then(() => mutate())
      },
      { yes: t("buttons.delete"), no: t("buttons.cancel") }
    )
  }

  useEffect(() => {
    let searchedData
    if (search) {
      searchedData = data?.filter(({ name }) => {
        return name?.toLowerCase()?.includes(search) || !search
      })
    } else {
      searchedData = data
    }
    setFilteredData(searchedData)
  }, [data, search])

  return (
    <div>
      <AddModule show={showAdd} onHide={hideAdd} mutate={mutate} />
      <EditModule data={modalData} show={showEdit} onHide={hideEdit} mutate={mutate} />
      <ModuleDetail data={modalData} show={showDetail} onHide={hideDetail} />
      <Spinner show={!Array.isArray(data)} />
      <TopBar>
        <Breadcrumb name={t("breadcrumb.settings")} link="" />
        <Breadcrumb name={t("breadcrumb.module")} link="/settings/module" />
      </TopBar>
      <Container>
        <div className="card">
          <div className="card-title pt-4">
            <SearchBox placeholder={t("placeholder.search_module")} onChange={(value) => setSearch(value?.toLowerCase())} />
            {can(Permission.SettingsModuleAdd) && (
              <Button variant="primary" onClick={onShowAdd}>
                {t("buttons.add_new_module")}
              </Button>
            )}
          </div>
          <div className="card-body">
            <table className="table table-center table-data">
              <thead>
                <tr>
                  <th>No</th>
                  <th>{t("table.name")}</th>
                  <th>{t("table.description")}</th>
                  <th>{t("table.regulation_reference")}</th>
                  {showActionColumn && <th>{t("table.action")}</th>}
                </tr>
              </thead>
              <tbody>
                {dataTable?.length ? (
                  dataTable?.map((module, index) => (
                    <tr key={module?.uuid}>
                      <td>{index + 1 + pages.page * pages.step}</td>
                      <td>{module?.name}</td>
                      <td>{module?.desc}</td>
                      <td>{module?.regulation_reference}</td>
                      {showActionColumn && (
                        <td>
                          {can(Permission.SettingsModuleDetail) && (
                            <Button variant="primary" title="Detail" onClick={() => handleDetail(module)} className="m-1" icon={BsEye} />
                          )}
                          {can(Permission.SettingsModuleEdit) && (
                            <Button variant="secondary" title="Edit" onClick={() => handleEdit(module)} className="m-1" icon={BsPencil} />
                          )}
                          {can(Permission.SettingsModuleDelete) && (
                            <Button
                              variant="tertiary"
                              title="Delete"
                              onClick={() => handleDelete(module?.uuid, module?.name)}
                              className="m-1"
                              icon={BsTrash}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={showActionColumn ? 5 : 4}>No data.</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="d-flex">
              <div className="ms-auto">
                <Pagination hook={pages} />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default SettingsModule
